/**
 * Console/error ring buffer for screenshot diagnostics.
 *
 * When a client captures a screenshot to attach to a ticket we want the
 * recent client-side errors that preceded it (plan §5.1 / D6). This module
 * keeps the last {@link BUFFER_SIZE} `console.error` / `console.warn` calls
 * plus uncaught errors and unhandled promise rejections in a bounded ring
 * buffer, so a support agent can see what the browser was complaining about.
 *
 * Only our own app's tab is observed — site-side capture is out of scope for
 * v1.2 (deferred to a future MainWP-extension version).
 *
 * Install once from `main.tsx` via {@link installConsoleBuffer}; read the
 * current snapshot with {@link getConsoleBuffer}.
 */

/** Level of a captured console/error entry. */
export type ConsoleEntryLevel = 'error' | 'warn' | 'unhandledrejection';

/** A single captured diagnostic entry. */
export interface ConsoleEntry {
  level: ConsoleEntryLevel;
  /** Message text, truncated to {@link MAX_MESSAGE_LEN} characters. */
  message: string;
  /** ISO-8601 timestamp of when the entry was captured. */
  ts: string;
}

/** Maximum number of entries retained (oldest evicted first). */
export const BUFFER_SIZE = 50;

/** Per-message character cap — long stack traces are truncated. */
export const MAX_MESSAGE_LEN = 2000;

const buffer: ConsoleEntry[] = [];
let installed = false;

/**
 * Coerce an arbitrary console/error argument list into a single string,
 * defensively — a thrown-inside-toString value must never break logging.
 */
function stringifyArgs(args: unknown[]): string {
  const parts = args.map((arg) => {
    if (typeof arg === 'string') return arg;
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    try {
      return JSON.stringify(arg) ?? String(arg);
    } catch {
      return String(arg);
    }
  });
  return parts.join(' ');
}

function push(level: ConsoleEntryLevel, message: string): void {
  buffer.push({
    level,
    message: message.slice(0, MAX_MESSAGE_LEN),
    // Date.now-free ISO stamp; new Date() is fine in the browser runtime.
    ts: new Date().toISOString(),
  });
  if (buffer.length > BUFFER_SIZE) buffer.shift();
}

/**
 * Install the global hooks. Idempotent — subsequent calls are no-ops so a
 * hot-reload or an accidental double-install cannot double-wrap `console`.
 */
export function installConsoleBuffer(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    push('error', stringifyArgs(args));
    originalError(...args);
  };
  console.warn = (...args: unknown[]): void => {
    push('warn', stringifyArgs(args));
    originalWarn(...args);
  };

  // addEventListener (not onerror assignment) so we never clobber any other
  // handler the page installs.
  window.addEventListener('error', (event: ErrorEvent) => {
    const detail =
      event.error instanceof Error ? event.error.stack || event.message : event.message;
    push('error', detail || 'Uncaught error');
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const detail =
      reason instanceof Error ? reason.stack || reason.message : stringifyArgs([reason]);
    push('unhandledrejection', detail || 'Unhandled promise rejection');
  });
}

/**
 * Return a shallow copy of the current buffer (oldest first). The copy keeps
 * callers from mutating internal state and is safe to serialize as metadata.
 */
export function getConsoleBuffer(): ConsoleEntry[] {
  return buffer.slice();
}

/** Clear the buffer. Exposed for tests and defensive resets. */
export function clearConsoleBuffer(): void {
  buffer.length = 0;
}
